// ./src/utils/eventDates.ts
//
// ROADMAP A4 — the DATES of a recurring event, as the app shows and offers them.
//
// Mirrors the server's rules (eventsmagic-api `core/recurringSales.ts`) so the app never offers a date
// the server will refuse: a canceled date, or one that has already ended, is not bookable — a date
// already under way still is (a door sale mid-session is a real sale).
import type { EventTimeSlot } from "@/utils/api";

export const RECURRING_SCHEDULE_MODE = "recurring";

/** Whether a date can still be sold or booked. */
export const isSlotBookable = (slot: EventTimeSlot | null | undefined, now: Date = new Date()): boolean => {
  if (!slot || slot.canceledAt) {
    return false;
  }

  const end = slot.endsAt ?? slot.startsAt;

  return !end || new Date(end).getTime() > now.getTime();
};

/** How a date is named: its own name, else its start in the EVENT's timezone ("Tue, Oct 6, 6:30 PM"). */
export const formatSlotLabel = (slot: EventTimeSlot | null | undefined, timezone?: string | null): string => {
  const name = (slot?.name ?? "").trim();
  if (name) {
    return name;
  }

  if (!slot?.startsAt) {
    return "—";
  }

  try {
    return new Intl.DateTimeFormat("en", {
      weekday: "short",
      month: "short",
      day: "numeric",
      ...(slot.isAllDay ? {} : { hour: "numeric", minute: "2-digit" }),
      timeZone: timezone || undefined,
    }).format(new Date(slot.startsAt));
  } catch {
    return new Date(slot.startsAt).toLocaleString();
  }
};

/** The bookable dates of an event, soonest first, as Select options. */
export const bookableSlotOptions = (slots: EventTimeSlot[], timezone?: string | null): Array<{ value: string; label: string }> =>
  slots
    .filter((s) => s.id && isSlotBookable(s))
    .sort((a, b) => new Date(a.startsAt ?? 0).getTime() - new Date(b.startsAt ?? 0).getTime())
    .map((s) => ({ value: s.id as string, label: formatSlotLabel(s, timezone) }));

/** A calendar day ("YYYY-MM-DD") as seen in a timezone. */
const dayInZone = (d: Date, timezone?: string | null): string => {
  try {
    return new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone: timezone || undefined }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
};

/**
 * How long before a session starts its door opens when nobody has said otherwise — mirrors the
 * server's DEFAULT_DOORS_OPEN_MINUTES. Since #41 this is only the FALLBACK: the event, and each of
 * its dates, may carry their own `doorsOpenMinutes`.
 */
export const DEFAULT_DOORS_OPEN_MINUTES = 90;

/** The widest door accepted, matching the server: a week. */
export const MAX_DOORS_OPEN_MINUTES = 7 * 24 * 60;

/**
 * Normalize one stored `doorsOpenMinutes` — a non-negative whole number, or null when unset.
 *
 * ⚠ 0 IS A REAL ANSWER ("doors open exactly at the start"), so this tests for null and NEVER for
 * falsiness. `value || 90` would silently turn a host's 0 into an hour and a half.
 */
const readDoorsOpenMinutes = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes < 0) {
    return null;
  }

  return Math.min(MAX_DOORS_OPEN_MINUTES, Math.round(minutes));
};

/**
 * How long before THIS date's start its door opens (#41) — mirrors the server's
 * `recurringSales.resolveDoorsOpenMinutes`: the DATE's setting, else the EVENT's, else the default.
 */
export const resolveDoorsOpenMinutes = (
  slot: { doorsOpenMinutes?: number | null } | null | undefined,
  event: { doorsOpenMinutes?: number | null } | null | undefined,
): number => {
  const fromSlot = readDoorsOpenMinutes(slot?.doorsOpenMinutes);
  if (fromSlot !== null) {
    return fromSlot;
  }

  const fromEvent = readDoorsOpenMinutes(event?.doorsOpenMinutes);
  if (fromEvent !== null) {
    return fromEvent;
  }

  return DEFAULT_DOORS_OPEN_MINUTES;
};

/**
 * Whether a session is OPEN at the door now — the server's `isDateCurrent` (recurringSales.ts): from `doorsOpenMinutes` before it
 * starts until it ends; with no end, until the end of its start's day in the EVENT's timezone; an all-day session from the
 * start of its day. ⚠ Not "the same day": a day can hold a 4 PM and a 7 PM show. The server decides; this only warns.
 *
 * ⚠ `doorsOpenMinutes` is REQUIRED (#41) and has no default, exactly as on the server: a defaulted argument is how a caller
 * keeps warning with 90 minutes after the host set something else, and a door that opens at the wrong time still opens.
 */
export const isSlotCurrent = (
  // Only the fields it reads, so a single-date EVENT (#178's dashboard card) is judged by the same rule.
  slot: Pick<EventTimeSlot, "startsAt" | "endsAt" | "isAllDay" | "canceledAt"> | null | undefined,
  now: Date,
  timezone: string | null | undefined,
  doorsOpenMinutes: number,
): boolean => {
  if (!slot || slot.canceledAt || !slot.startsAt) {
    return false;
  }

  const start = new Date(slot.startsAt);
  const sameDay = dayInZone(start, timezone) === dayInZone(now, timezone);
  const opened = slot.isAllDay ? sameDay || now.getTime() >= start.getTime() : now.getTime() >= start.getTime() - doorsOpenMinutes * 60000;
  if (!opened) {
    return false;
  }

  if (slot.endsAt) {
    return now.getTime() <= new Date(slot.endsAt).getTime();
  }

  return sameDay;
};

/** When this date's door opens, or null when it has no start. Mirrors the server's `doorsOpenAt`. */
export const slotDoorsOpenAt = (
  slot: EventTimeSlot | null | undefined,
  doorsOpenMinutes: number,
): Date | null => {
  if (!slot?.startsAt) {
    return null;
  }

  const start = new Date(slot.startsAt);

  // An all-day date opens with its day — there is no start time to count back from. The exact
  // instant is the server's to state (it knows the event's timezone); this is only the warning path.
  return slot.isAllDay ? start : new Date(start.getTime() - doorsOpenMinutes * 60000);
};

const byStart = (a: EventTimeSlot, b: EventTimeSlot): number => new Date(a.startsAt ?? 0).getTime() - new Date(b.startsAt ?? 0).getTime();

/** The dates a door can check in to: every date not canceled, soonest first (a past one needs an override). */
export const doorSlotOptions = (slots: EventTimeSlot[], timezone?: string | null): Array<{ value: string; label: string }> =>
  slots
    .filter((s) => s.id && !s.canceledAt && s.startsAt)
    .sort(byStart)
    .map((s) => ({ value: s.id as string, label: formatSlotLabel(s, timezone) }));

/** The session a door opens on: the one open now, else the next to come, else the most recent. */
export const defaultDoorSlotId = (
  slots: EventTimeSlot[],
  timezone: string | null | undefined,
  event: { doorsOpenMinutes?: number | null } | null | undefined,
  now: Date = new Date(),
): string | null => {
  const live = slots.filter((s) => s.id && !s.canceledAt && s.startsAt).sort(byStart);
  const today = live.find((s) => isSlotCurrent(s, now, timezone, resolveDoorsOpenMinutes(s, event)));
  if (today) {
    return today.id as string;
  }

  const next = live.find((s) => new Date(s.startsAt as string).getTime() > now.getTime());
  if (next) {
    return next.id as string;
  }

  return live.length > 0 ? (live[live.length - 1].id as string) : null;
};

/**
 * #179 — the date a REPORT opens on: the one on now (doors included), else the latest that has STARTED, else the next.
 * ⚠ Not the door's pick: a door looks forward (the next date), a report looks back — opened on Wednesday to review
 * Monday, the door's choice showed next Monday with nobody in it (review).
 */
export const defaultReportSlotId = (
  slots: EventTimeSlot[],
  timezone: string | null | undefined,
  event: { doorsOpenMinutes?: number | null } | null | undefined,
  now: Date = new Date(),
): string | null => {
  const live = slots.filter((s) => s.id && !s.canceledAt && s.startsAt).sort(byStart);
  const today = live.find((s) => isSlotCurrent(s, now, timezone, resolveDoorsOpenMinutes(s, event)));
  if (today) {
    return today.id as string;
  }

  const started = live.filter((s) => new Date(s.startsAt as string).getTime() <= now.getTime());
  if (started.length > 0) {
    return started[started.length - 1].id as string;
  }

  return live.length > 0 ? (live[0].id as string) : null;
};

// ── "Doors open" presets (#41) ───────────────────────────────────────────────

/** "at the start" / "45 minutes before" / "1 hour 30 minutes before" — how a lead time reads. */
export const formatDoorsOpenLead = (minutes: number): string => {
  if (minutes <= 0) {
    return "at the start";
  }

  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  }

  if (rest > 0) {
    parts.push(`${rest} ${rest === 1 ? "minute" : "minutes"}`);
  }

  return `${parts.join(" ")} before`;
};

/** The lead times offered in the picker. Minutes throughout — hours are only how they read. */
const DOORS_OPEN_PRESET_MINUTES = [0, 15, 30, 45, 60, 90, 120, 180, 240, 360, 720, 1440];

/**
 * Options for a "doors open" picker. `""` is the INHERIT choice — the date falls back to the event,
 * the event to the default — and the caller supplies its wording. A stored value that is not one of
 * the presets is added as its own option, so opening the form cannot silently round it away.
 */
export const doorsOpenOptions = (
  current: number | null | undefined,
  inheritLabel: string,
): Array<{ value: string; label: string }> => {
  const options = [
    { value: "", label: inheritLabel },
    ...DOORS_OPEN_PRESET_MINUTES.map((m) => ({ value: String(m), label: formatDoorsOpenLead(m) })),
  ];

  const stored = readDoorsOpenMinutes(current);
  if (stored !== null && !DOORS_OPEN_PRESET_MINUTES.includes(stored)) {
    options.push({ value: String(stored), label: formatDoorsOpenLead(stored) });
  }

  return options;
};
